import { cn } from "@/lib/utils";
import type { Policy } from "@shared/schema";
import { IN_FORCE_STATUSES } from "@shared/schema";
import { AOR_INSURED_TOOLTIP, AorIcon, isAorFlagged } from "./policies/aor";

interface Props {
  policies: Policy[];
  className?: string;
  // CSR-2327 — the AOR experience flag, read once by the insured page.
  showAor?: boolean;
}

function isInForceToday(p: Policy): boolean {
  if (!IN_FORCE_STATUSES.includes((p.status?.toLowerCase() ?? "") as typeof IN_FORCE_STATUSES[number])) {
    return false;
  }
  if (!p.effectiveDate || !p.expirationDate) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const eff = new Date(p.effectiveDate + "T00:00:00").getTime();
  const exp = new Date(p.expirationDate + "T00:00:00").getTime();
  return eff <= today && exp >= today;
}

function expiresWithinDays(p: Policy, days: number): boolean {
  if (!p.expirationDate) return false;
  const exp = new Date(p.expirationDate + "T00:00:00").getTime();
  const now = Date.now();
  const diffDays = (exp - now) / 86_400_000;
  return diffDays >= 0 && diffDays <= days;
}

export function hasInsuredStatus(policies: Policy[], showAor = false): boolean {
  // Since Renewal requires an in-force policy (checked inside the component),
  // the Status block only shows when there's at least one in-force policy today
  // — or, CSR-2327, when a policy is flagged AOR'd away: the flag outlives the
  // term, so it must still reach the sidebar once nothing is in force.
  return (
    policies.some(isInForceToday) || (showAor && policies.some(isAorFlagged))
  );
}

export function InsuredStatusBadges({ policies, className, showAor = false }: Props) {
  const inForce = policies.filter(isInForceToday);
  const isActive = inForce.length > 0;
  const isRenewal = inForce.some((p) => expiresWithinDays(p, 60));

  const showAorIcon = showAor && policies.some(isAorFlagged);

  if (!isActive && !isRenewal && !showAorIcon) return null;

  const pill = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold";
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)} data-testid="insured-status-badges">
      {isActive && (
        <span className={cn(pill, "bg-emerald-500/10 text-emerald-600")}>
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
        </span>
      )}
      {isRenewal && (
        <span className={cn(pill, "bg-muted text-muted-foreground")}>Renewal</span>
      )}
      {showAorIcon && (
        <AorIcon
          tooltip={AOR_INSURED_TOOLTIP}
          testId="insured-aor-indicator"
          className="self-center"
        />
      )}
    </div>
  );
}
